import type { CatalogTemplate } from '~/store/billing'

/**
 * What the image picker knows about the catalog that the catalog itself does
 * not say: which shelf an app sits on, which apps open the marketplace, which
 * colour a brand mark is painted in, how operating systems are ordered, and
 * whether the chosen plan can run an image.
 *
 * Pure on purpose — no auto-imports, no Vue — so `node --test` can import it
 * (`tests/checkout/`) and the two consumers, the checkout and the reinstall
 * modal, cannot drift apart: both render `TemplatePicker`, and it reads these.
 *
 * The API models none of this. An app is a template whose `category` is its own
 * product name (104 categories for 105 apps), so "which shelf" can only be said
 * here and on the website's storefront (`../website/lib/marketplace/listing.ts`),
 * and the two must say the same thing: a customer who browses "Telefonia e
 * PABX" there and meets the same shelves in the checkout has to find the same
 * apps under each.
 */

export type TemplateShelf =
  | 'hosting'
  | 'telephony'
  | 'developer'
  | 'ai'
  | 'business'
  | 'database'
  | 'communications'
  | 'networking'
  | 'content'
  | 'games'
  | 'monitoring'
  | 'security'
  | 'other'

/** What the marketplace is showing: a curated view, everything, or one shelf. */
export type TemplateShelfFilter = 'featured' | 'new' | 'all' | TemplateShelf

/** Shelf order, identical to the website's `APP_GROUP_ORDER`. */
export const TEMPLATE_SHELF_ORDER: readonly TemplateShelf[] = [
  'hosting',
  'telephony',
  'developer',
  'ai',
  'business',
  'database',
  'communications',
  'networking',
  'content',
  'games',
  'monitoring',
  'security',
  'other',
]

/**
 * A pictogram and an accent per shelf. The accent paints the apps whose icon is
 * a pictogram rather than a brand mark (14 of the platform's apps have no
 * bundled logo), so a shelf reads as one family instead of a wall of grey.
 */
export const TEMPLATE_SHELF_STYLE: Record<TemplateShelf, { icon: string; accent: string }> = {
  hosting: { icon: 'i-lucide-panels-top-left', accent: '#0284c7' },
  telephony: { icon: 'i-lucide-phone-call', accent: '#16a34a' },
  developer: { icon: 'i-lucide-code-xml', accent: '#4f46e5' },
  ai: { icon: 'i-lucide-sparkles', accent: '#9333ea' },
  business: { icon: 'i-lucide-briefcase', accent: '#d97706' },
  database: { icon: 'i-lucide-database', accent: '#0891b2' },
  communications: { icon: 'i-lucide-messages-square', accent: '#0d9488' },
  networking: { icon: 'i-lucide-network', accent: '#2563eb' },
  content: { icon: 'i-lucide-layout-template', accent: '#db2777' },
  games: { icon: 'i-lucide-gamepad-2', accent: '#7c3aed' },
  monitoring: { icon: 'i-lucide-activity', accent: '#ea580c' },
  security: { icon: 'i-lucide-shield-check', accent: '#475569' },
  other: { icon: 'i-lucide-box', accent: '#64748b' },
}

/**
 * The platform's own marketplace apps (the API's `src/templates/marketplace`),
 * by `templates.code` — the one thing about a template that never changes — so
 * each lands on its shelf whatever its name says. The website's
 * `MARKETPLACE_APP_GROUPS` holds the same table: an app added to the API's
 * catalog is added to both, or it lands on a regex guess below.
 */
export const MARKETPLACE_APP_GROUPS: Readonly<Record<string, TemplateShelf>> = {
  'ai-coding-agents': 'ai',
  'anythingllm': 'ai',
  'dify': 'ai',
  'hermes-agent': 'ai',
  'langflow': 'ai',
  'open-webui': 'ai',
  'openclaw': 'ai',
  'airflow': 'developer',
  'appwrite': 'developer',
  'caprover': 'developer',
  'code-server': 'developer',
  'coolify': 'developer',
  'directus': 'developer',
  'django': 'developer',
  'docker-ce': 'developer',
  'dokku': 'developer',
  'dokploy': 'developer',
  'flask': 'developer',
  'gitea': 'developer',
  'gitlab': 'developer',
  'jenkins': 'developer',
  'jupyterlab': 'developer',
  'k3s': 'developer',
  'laravel': 'developer',
  'mean': 'developer',
  'mern': 'developer',
  'mosquitto': 'developer',
  'n8n': 'developer',
  'node-red': 'developer',
  'nodejs': 'developer',
  'portainer': 'developer',
  'rabbitmq': 'developer',
  'rails': 'developer',
  'sonarqube': 'developer',
  'strapi': 'developer',
  'supabase': 'developer',
  '1panel': 'hosting',
  'centos-7-x64-13': 'hosting', // CyberPanel
  'centos-7-x64-16': 'hosting', // HestiaCP
  'cloudpanel': 'hosting',
  'cloudron': 'hosting',
  'cpanel-whm': 'hosting',
  'cwp': 'hosting',
  'directadmin': 'hosting',
  'easypanel': 'hosting',
  'fastpanel': 'hosting',
  'froxlor': 'hosting',
  'ispconfig': 'hosting',
  'kusanagi': 'hosting',
  'lamp': 'hosting',
  'lemp': 'hosting',
  'plesk': 'hosting',
  'tinycp': 'hosting',
  'virtualmin': 'hosting',
  'webmin': 'hosting',
  'webuzo': 'hosting',
  'dolibarr': 'business',
  'erpnext': 'business',
  'glpi': 'business',
  'invoice-ninja': 'business',
  'mautic': 'business',
  'metabase': 'business',
  'nocodb': 'business',
  'odoo': 'business',
  'onlyoffice-docs': 'business',
  'openproject': 'business',
  'paperless-ngx': 'business',
  'prestashop': 'business',
  'redmine': 'business',
  'snipe-it': 'business',
  'traccar': 'business',
  'woocommerce': 'business',
  'chatwoot': 'communications',
  'discourse': 'communications',
  'evolution-api': 'communications',
  'jitsi-meet': 'communications',
  'listmonk': 'communications',
  'mattermost': 'communications',
  'ntfy': 'communications',
  'rocketchat': 'communications',
  'typebot': 'communications',
  'waha': 'communications',
  'adguard-home': 'networking',
  'genieacs': 'networking',
  'guacamole': 'networking',
  'meshcentral': 'networking',
  'netbox': 'networking',
  'nginx-proxy-manager': 'networking',
  'openvpn-access-server': 'networking',
  'phpipam': 'networking',
  'pihole': 'networking',
  'pritunl': 'networking',
  'rustdesk-server': 'networking',
  'wg-easy': 'networking',
  'ant-media-server': 'content',
  'azuracast': 'content',
  'bookstack': 'content',
  'drupal': 'content',
  'ghost': 'content',
  'immich': 'content',
  'jellyfin': 'content',
  'joomla': 'content',
  'moodle': 'content',
  'nextcloud': 'content',
  'openlitespeed-wordpress': 'content',
  'owncast': 'content',
  'plex': 'content',
  'seafile': 'content',
  'wikijs': 'content',
  'wordpress': 'content',
  'elasticsearch': 'monitoring',
  'grafana': 'monitoring',
  'graylog': 'monitoring',
  'librenms': 'monitoring',
  'matomo': 'monitoring',
  'signoz': 'monitoring',
  'umami': 'monitoring',
  'uptime-kuma': 'monitoring',
  'zabbix-7-lts': 'monitoring',
  'authentik': 'security',
  'keycloak': 'security',
  'openbao': 'security',
  'vaultwarden': 'security',
  'wazuh': 'security',
  'mongodb': 'database',
  'mysql': 'database',
  'mysql-8-lts': 'database',
  'mysql-9-lts': 'database',
  'postgresql': 'database',
  'redis': 'database',
  'sql-server-2025': 'database',
  'valkey': 'database',
  'counter-strike-2': 'games',
  'fivem': 'games',
  'minecraft-bedrock': 'games',
  'minecraft-java': 'games',
  'palworld': 'games',
  'pterodactyl': 'games',
  // Shelved here explicitly: no word in its name picks a shelf, and it is not a
  // remote-access tool so much as a desktop to work on.
  'ubuntu-desktop': 'other',
}

/**
 * The templates made by hand before the API's catalog existed, by code for the
 * same reason: their names are editorial and change — "Hestia CP Hosting"
 * becoming "HestiaCP" would otherwise drop it off the hosting shelf, whose
 * pattern matched the word "hosting". The website's `LEGACY_APP_GROUPS` is the
 * same table.
 */
export const LEGACY_APP_GROUPS: Readonly<Record<string, TemplateShelf>> = {
  '3cx': 'telephony',
  'erix-pabx-ip': 'telephony',
  'freepbx': 'telephony',
  'issabel': 'telephony',
  'issabel-43': 'telephony',
  'yeastar-p-series-software-pbx': 'telephony',
  'vicibox-12': 'telephony',
  'ubuntu-server-22-04-x64': 'hosting', // aaPanel
  'asp-net-core-nginx': 'developer',
  'mikrotik-routeros-7': 'networking',
  'mkauth': 'networking',
  'pfsense-2-7': 'networking',
  'zentyal-8-development': 'networking',
  'kali-linux': 'security',
}

type TemplateText = Pick<CatalogTemplate, 'code' | 'name' | 'category' | 'version_label'>

function templateText(template: TemplateText): string {
  return `${template.name} ${template.version_label ?? ''} ${template.category} ${template.code}`.toLowerCase()
}

/**
 * Which shelf an app sits on. The tables decide every app the catalog holds
 * today; the patterns only guess for one published later, the same the day it
 * appears in the Super Admin Console as on the website — so a new app is on
 * sale in the right place before anyone edits either table.
 */
export function templateShelf(template: TemplateText): TemplateShelf {
  const known = MARKETPLACE_APP_GROUPS[template.code] ?? LEGACY_APP_GROUPS[template.code]
  if (known) return known

  const value = templateText(template)
  if (/mariadb|mysql|mongodb|postgres|redis|mssql|sql server/.test(value)) return 'database'
  if (/3cx|freepbx|issabel|pabx|pbx|yeastar|asterisk|vicibox|vicidial/.test(value)) return 'telephony'
  if (/mk-?auth|mikrotik|routeros|pfsense|zentyal/.test(value)) return 'networking'
  if (/kali/.test(value)) return 'security'
  if (/wordpress|nextcloud/.test(value)) return 'content'
  if (/zabbix/.test(value)) return 'monitoring'
  if (/minecraft|counter-strike|fivem|palworld|valheim|terraria|game server|servidor de jogo/.test(value)) return 'games'
  if (/asp\.net|coolify|dokploy|gitlab|n8n|portainer/.test(value)) return 'developer'
  if (/panel|cpanel|webmin|webuzo|tinycp|kusanagi|hosting/.test(value)) return 'hosting'
  return 'other'
}

/**
 * The apps the marketplace opens on, in order. A merchandising decision kept in
 * code (the house rule for a fact no row would ever hold differently): the
 * installed base says telephony and hosting panels, the new catalog says
 * automation, WhatsApp and AI. Codes the catalog does not hold are skipped, so
 * an install without one of them simply shows one fewer.
 */
export const FEATURED_APP_CODES: readonly string[] = [
  'wordpress',
  'n8n',
  'evolution-api',
  'openclaw',
  'issabel-43',
  'ubuntu-server-22-04-x64',
  'coolify',
  'zabbix-7-lts',
]

/** Position in the featured list, or Infinity. Ties search results. */
export function featuredRank(code: string): number {
  const index = FEATURED_APP_CODES.indexOf(code)
  return index === -1 ? Number.POSITIVE_INFINITY : index
}

/** How long an app reads as "Novo" after it joins the catalog. */
export const NEW_TEMPLATE_DAYS = 60

/**
 * Whether an app joined the catalog recently. `created_at` is the row's own
 * fact (published by the API since the picker needed it); an API that does not
 * send it yet makes nothing new, which is the honest reading.
 */
export function isNewTemplate(
  template: Pick<CatalogTemplate, 'template_type'> & { created_at?: string | null },
  nowMs: number,
): boolean {
  if (template.template_type !== 'MARKETPLACE_APP' || !template.created_at) return false
  const created = Date.parse(template.created_at)
  if (Number.isNaN(created)) return false
  const age = nowMs - created
  return age >= 0 && age < NEW_TEMPLATE_DAYS * 24 * 60 * 60 * 1000
}

/**
 * An app's version, when its label IS one. The API derives `version_label` from
 * the display name when staff have not curated it, which for an app yields
 * "(WhatsApp)", "+ Ollama" or "CE" — the product's name, not its release. Only
 * a label that starts with a digit names a version; the website applies the
 * same rule (`realVersion()`). And a name that already says it ("Issabel 5",
 * "Zabbix 7.0 LTS") needs no second copy beside it.
 */
export function appVersionLabel(template: Pick<CatalogTemplate, 'name' | 'version_label'>): string | null {
  const label = template.version_label?.trim()
  if (!label || !/^\d/.test(label)) return null
  return template.name.includes(label) ? null : label
}

// ── Operating systems ─────────────────────────────────────────────────────

export type OsSection = 'linux' | 'windows' | 'other'

/**
 * Windows leads because it is what most customers order: ~62% of the new
 * platform's first month of orders and ~74% of the installed fleet.
 */
export const OS_SECTION_ORDER: readonly OsSection[] = ['windows', 'linux', 'other']

export function osSection(template: Pick<CatalogTemplate, 'os_family'>): OsSection {
  return template.os_family === 'linux' || template.os_family === 'windows'
    ? template.os_family
    : 'other'
}

/**
 * Families pinned to the front of their section. Ubuntu and Debian are what most
 * Linux buyers come for; Windows Server is ~68% of the fleet's installed
 * operating systems and used to be the tenth tile of ten, behind an alphabet.
 */
const PINNED_FAMILIES: Record<OsSection, readonly string[]> = {
  linux: ['Ubuntu', 'Debian'],
  windows: ['Windows Server', 'Windows'],
  other: [],
}

/**
 * Order of OS families: section (Windows, Linux, the rest), then the pinned
 * families, then alphabetical. The website's `lib/marketplace/os-family-order.ts`
 * implements the same rule, so the two storefronts agree on what comes first.
 */
export function compareOsFamilies(
  a: { category: string; section: OsSection },
  b: { category: string; section: OsSection },
): number {
  const sectionDelta = OS_SECTION_ORDER.indexOf(a.section) - OS_SECTION_ORDER.indexOf(b.section)
  if (sectionDelta !== 0) return sectionDelta
  const pinned = PINNED_FAMILIES[a.section]
  const aPin = pinned.indexOf(a.category)
  const bPin = pinned.indexOf(b.category)
  if (aPin !== bPin) {
    if (aPin === -1) return 1
    if (bPin === -1) return -1
    return aPin - bPin
  }
  return a.category.localeCompare(b.category)
}

/** `Windows Server` → `windows-server`: the key an OS family's copy lives under. */
export function osFamilySlug(category: string): string {
  return category
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

/**
 * Which version of a distribution a customer who expressed no preference gets.
 *
 * Staff's `is_recommended` first, then the newest version still receiving
 * security updates, and only then the newest of all — the list is already in
 * newest-first order. Taking `versions[0]` unconditionally meant clicking
 * "CentOS" handed out CentOS Stream 9 or CentOS 7 depending only on which
 * happened to sort first.
 */
export function defaultVersion<T extends Pick<CatalogTemplate, 'is_recommended' | 'support_status'>>(
  versions: readonly T[],
): T | undefined {
  return (
    versions.find((template) => template.is_recommended) ??
    versions.find((template) => template.support_status !== 'ended') ??
    versions[0]
  )
}

/**
 * How a release's support window reads: an icon and a colour per status,
 * always shipped WITH words. `null` is absent on purpose — the platform has
 * said nothing about that release, and a neutral "supported" chip would be a
 * reassurance nobody checked. The 700 shade (400 in dark mode) because the
 * bare `text-success` and `text-warning` are the 500 shade, about 2:1 on white.
 * Classes are written out in full: Tailwind finds utilities by scanning text.
 */
export const SUPPORT_TONE = {
  supported: { class: 'text-success-700 dark:text-success-400', icon: 'i-lucide-shield-check' },
  ending_soon: { class: 'text-warning-700 dark:text-warning-400', icon: 'i-lucide-clock-alert' },
  ended: { class: 'text-error-700 dark:text-error-400', icon: 'i-lucide-shield-off' },
} as const

/** `2029-05-31` → "maio de 2029" / "May 2029": an end-of-support date, as a month, in the reader's locale. */
export function eolMonthLabel(value: string | null, locale: string): string {
  if (!value) return ''
  const parsed = new Date(`${value}T00:00:00.000Z`)
  if (Number.isNaN(parsed.getTime())) return value
  return parsed.toLocaleDateString(locale, { month: 'long', year: 'numeric', timeZone: 'UTC' })
}

// ── Does the configuration run it? ───────────────────────────────────────

export type TemplateSpecs = {
  vcpu: number | null
  ramMb: number | null
  /** Total main disk in GB. Extra data disks never hold the OS. */
  mainDiskGb: number | null
  /**
   * Whether the flow can still grow the main disk. Checkout can — its storage
   * step raises the disk to the floor and says so — so disk is never a
   * shortfall there. A reinstall restores onto the disk that exists, so there
   * it is one.
   */
  diskAdjustable: boolean
}

export type TemplateShortfall = { ramMb: number | null; vcpu: number | null; diskGb: number | null }

/**
 * What the configuration is short of to run an image, or null when it fits.
 * An unknown value (no plan chosen yet) is never a shortfall: an unknown plan
 * must not read as a failing one.
 */
export function templateFit(
  template: Pick<CatalogTemplate, 'min_ram_mb' | 'min_vcpu' | 'min_disk_gb'>,
  specs: TemplateSpecs,
): TemplateShortfall | null {
  const ramMb =
    template.min_ram_mb != null && specs.ramMb != null && specs.ramMb < template.min_ram_mb
      ? template.min_ram_mb
      : null
  const vcpu =
    template.min_vcpu != null && specs.vcpu != null && specs.vcpu < template.min_vcpu
      ? template.min_vcpu
      : null
  const diskGb =
    !specs.diskAdjustable &&
    template.min_disk_gb != null &&
    specs.mainDiskGb != null &&
    specs.mainDiskGb < template.min_disk_gb
      ? template.min_disk_gb
      : null
  return ramMb !== null || vcpu !== null || diskGb !== null ? { ramMb, vcpu, diskGb } : null
}

/** "4 GB RAM · 2 vCPU · 40 GB" — what a shortfall (or a minimum) asks for. */
export function specLabel(spec: { ramMb?: number | null; vcpu?: number | null; diskGb?: number | null }): string {
  const parts: string[] = []
  if (spec.vcpu != null) parts.push(`${spec.vcpu} vCPU`)
  if (spec.ramMb != null) parts.push(`${formatRamGb(spec.ramMb)} GB RAM`)
  if (spec.diskGb != null) parts.push(`${spec.diskGb} GB`)
  return parts.join(' · ')
}

function formatRamGb(ramMb: number): string {
  const gb = ramMb / 1024
  return Number.isInteger(gb) ? String(gb) : gb.toFixed(1)
}

// ── Icons and brand colour ────────────────────────────────────────────────

const ICON_NAME = /^i-[a-z0-9]+(?:-[a-z0-9]+)+$/
const HTTP_URL = /^https?:\/\//i

/**
 * What a template's `icon_url` means. Staff store either an Iconify name from a
 * bundled collection or an https URL (for the brands no icon set carries); the
 * API validates that on write, so anything else is legacy data and quietly falls
 * back rather than showing a broken image to a customer.
 */
export function resolveTemplateIcon(
  iconUrl: string | null | undefined,
  fallback: string,
): { kind: 'icon' | 'url'; value: string } {
  const value = iconUrl?.trim()
  if (value && ICON_NAME.test(value)) return { kind: 'icon', value }
  if (value && HTTP_URL.test(value)) return { kind: 'url', value }
  return { kind: 'icon', value: fallback }
}

/**
 * Icon for a template when staff have chosen none (`icon_url` null — 17 of the
 * hand-made apps today). The website's `heuristicIconName()` answers the same
 * question; the shelf pictogram is the last resort, so no tile is ever blank.
 */
export function fallbackIcon(template: TemplateText & Pick<CatalogTemplate, 'template_type'>): string {
  const value = templateText(template)

  if (template.template_type === 'MARKETPLACE_APP') {
    if (value.includes('mysql') || value.includes('mariadb')) return 'i-simple-icons-mariadb'
    if (value.includes('mongodb')) return 'i-simple-icons-mongodb'
    if (value.includes('postgres')) return 'i-simple-icons-postgresql'
    if (value.includes('redis')) return 'i-simple-icons-redis'
    if (value.includes('wordpress')) return 'i-simple-icons-wordpress'
    if (value.includes('gitlab')) return 'i-simple-icons-gitlab'
    if (value.includes('nextcloud')) return 'i-simple-icons-nextcloud'
    if (value.includes('n8n')) return 'i-simple-icons-n8n'
    if (value.includes('portainer')) return 'i-simple-icons-portainer'
    if (value.includes('cpanel')) return 'i-simple-icons-cpanel'
    if (value.includes('kali')) return 'i-simple-icons-kalilinux'
    if (value.includes('asterisk') || value.includes('issabel') || value.includes('freepbx')) {
      return 'i-simple-icons-asterisk'
    }
    return TEMPLATE_SHELF_STYLE[templateShelf(template)].icon
  }

  if (value.includes('ubuntu')) return 'i-simple-icons-ubuntu'
  if (value.includes('debian')) return 'i-simple-icons-debian'
  if (value.includes('centos')) return 'i-simple-icons-centos'
  if (value.includes('rocky')) return 'i-simple-icons-rockylinux'
  if (value.includes('alma')) return 'i-simple-icons-almalinux'
  if (value.includes('fedora')) return 'i-simple-icons-fedora'
  if (value.includes('suse')) return 'i-simple-icons-opensuse'
  if (value.includes('windows')) return 'i-simple-icons-windows'
  if (value.includes('freebsd')) return 'i-simple-icons-freebsd'
  if (value.includes('oracle')) return 'i-simple-icons-oracle'
  if (value.includes('cloudlinux') || value.includes('cloud linux')) return 'i-logos-cloudlinux'
  return 'i-lucide-server'
}

/**
 * Brand colours by simple-icons slug. Iconify ships the SHAPE of a mark and not
 * its hue, so a grid of 105 logos rendered in one ink reads as a wall; the
 * website inlines the same colours from the `simple-icons` package. Windows and
 * Oracle were dropped by simple-icons and keep their brand blue and red, as on
 * the website; SonarQube and SQL Server likewise. A slug missing here renders in the shelf
 * accent, never uncoloured.
 */
export const BRAND_COLORS: Readonly<Record<string, string>> = {
  '1panel': '#0854C1',
  adguard: '#68BC71',
  almalinux: '#000000',
  angular: '#0F0F11',
  apache: '#D22128',
  apacheairflow: '#017CEE',
  apacheguacamole: '#578B34',
  appwrite: '#FD366E',
  asterisk: '#F68F1E',
  authentik: '#FD4B2D',
  bookstack: '#0288D1',
  caprover: '#ED5B26',
  centos: '#262577',
  chatwoot: '#1F93FF',
  coder: '#090B0B',
  coolify: '#6B16ED',
  counterstrike: '#000000',
  cpanel: '#FF6C2C',
  debian: '#A81D33',
  directus: '#263238',
  discourse: '#000000',
  django: '#092E20',
  docker: '#2496ED',
  dolibarr: '#263C5C',
  dotnet: '#512BD4',
  drupal: '#0678BE',
  eclipsemosquitto: '#3C5280',
  elasticsearch: '#005571',
  erpnext: '#0089FF',
  fedora: '#51A2DA',
  fivem: '#F40552',
  flask: '#3BABC3',
  freebsd: '#AB2B28',
  ghost: '#15171A',
  gitea: '#609926',
  gitlab: '#FC6D26',
  grafana: '#F46800',
  graylog: '#FF3633',
  immich: '#4250AF',
  invoiceninja: '#000000',
  jellyfin: '#00A4DC',
  jenkins: '#D24939',
  jitsi: '#97979A',
  joomla: '#5091CD',
  jupyter: '#F37626',
  k3s: '#FFC61C',
  kalilinux: '#557C94',
  keycloak: '#4D4D4D',
  langflow: '#000000',
  listmonk: '#0055D4',
  mariadb: '#003545',
  matomo: '#3152A0',
  mattermost: '#0058CC',
  mautic: '#4E5E9E',
  metabase: '#509EE3',
  microsoftsqlserver: '#CC2927',
  mikrotik: '#293239',
  mongodb: '#47A248',
  mysql: '#4479A1',
  moodle: '#F98012',
  n8n: '#EA4B71',
  nextcloud: '#0082C9',
  nginx: '#009639',
  nginxproxymanager: '#F15833',
  nodedotjs: '#5FA04E',
  nodered: '#8F0000',
  ntfy: '#317F6F',
  odoo: '#714B67',
  ollama: '#000000',
  onlyoffice: '#444444',
  openproject: '#0770B8',
  opensuse: '#73BA25',
  oracle: '#C74634',
  paperlessngx: '#17541F',
  pfsense: '#212121',
  pihole: '#96060C',
  plesk: '#52BBE6',
  plex: '#EBAF00',
  portainer: '#13BEF9',
  postgresql: '#4169E1',
  prestashop: '#DF0067',
  rabbitmq: '#FF6600',
  react: '#61DAFB',
  redis: '#FF4438',
  redmine: '#B32024',
  rocketdotchat: '#F5455C',
  rockylinux: '#10B981',
  rubyonrails: '#D30001',
  rustdesk: '#024EFF',
  seafile: '#FF9800',
  sonarqube: '#4E9BCD',
  strapi: '#4945FF',
  supabase: '#3FCF8E',
  traccar: '#000000',
  ubuntu: '#E95420',
  umami: '#000000',
  uptimekuma: '#5CDD8B',
  vaultwarden: '#000000',
  webmin: '#7DA0D0',
  wikidotjs: '#1976D2',
  windows: '#0078D4',
  wireguard: '#88171A',
  woocommerce: '#96588A',
  wordpress: '#21759B',
}

/**
 * The colour to paint an icon in, or null for one that carries its own colours
 * (`i-logos-*`) or is an image. A brand mark takes its brand; a pictogram takes
 * the accent it is given (the app's shelf).
 */
export function iconColor(iconName: string, accent: string | null): string | null {
  if (iconName.startsWith('i-logos-')) return null
  if (iconName.startsWith('i-simple-icons-')) {
    return BRAND_COLORS[iconName.slice('i-simple-icons-'.length)] ?? accent
  }
  return accent
}

/**
 * One brand colour as two tones: the one to paint on a light surface and the
 * one to paint on a dark surface. Brands are chosen for print, not for either
 * theme — nine of the catalog's marks are black or nearly black and vanish on a
 * dark card, and k3s' yellow vanishes on a white one — so each is moved toward
 * contrast only as far as its luminance needs.
 */
export function brandTones(hex: string): { light: string; dark: string } {
  const rgb = parseHex(hex)
  if (!rgb) return { light: hex, dark: hex }
  const luminance = relativeLuminance(rgb)
  const light = luminance > 0.5 ? toHex(mix(rgb, [0, 0, 0], 0.3)) : toHex(rgb)
  const dark =
    luminance < 0.06
      ? toHex(mix(rgb, [255, 255, 255], 0.6))
      : luminance < 0.18
        ? toHex(mix(rgb, [255, 255, 255], 0.3))
        : toHex(rgb)
  return { light, dark }
}

type Rgb = [number, number, number]

function parseHex(hex: string): Rgb | null {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex.trim())
  if (!match) return null
  const value = Number.parseInt(match[1]!, 16)
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255]
}

function relativeLuminance([r, g, b]: Rgb): number {
  const channel = (c: number) => {
    const s = c / 255
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4
  }
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b)
}

function mix(a: Rgb, b: Rgb, weightOfB: number): Rgb {
  return [0, 1, 2].map((i) => Math.round(a[i]! * (1 - weightOfB) + b[i]! * weightOfB)) as Rgb
}

function toHex(rgb: Rgb): string {
  return `#${rgb.map((c) => c.toString(16).padStart(2, '0')).join('')}`
}

/**
 * Everything a picker surface renders about one template, resolved once by
 * `TemplatePicker` (the copy from the locale files, the fit against the plan)
 * so a card, a search result and the selected summary cannot say different
 * things about the same image.
 */
export type TemplateCardView = {
  code: string
  template: CatalogTemplate
  /** Apps only; null for an operating system. */
  shelf: TemplateShelf | null
  /** The shelf for an app, the distribution for an operating system. */
  groupLabel: string
  tagline: string
  keywords: string
  /** A real version for an app, the version label for an operating system. */
  version: string | null
  isNew: boolean
  /** What the chosen configuration is short of, or null. */
  shortfall: TemplateShortfall | null
  /** "4 GB RAM · 2 vCPU" while no plan is chosen and the image has minimums. */
  minimum: string | null
  fallbackIcon: string
  accent: string
}

/**
 * The i18n key segment for a template code. vue-i18n resolves `.` as a path
 * separator, so a code carrying one would be silently unreachable.
 */
export function catalogCopyKey(code: string): string {
  return code.replace(/\./g, '_')
}
